// Note, this file is more/less a copy of chunks within NodeConfig.jsx.  It is
// intended that NodeConfig.jsx be rewritten to utilize this file, Icon,
// Title, Description, and Property.
import React from 'react';
import { Accordion, Tab } from 'react-bootstrap';
import { Panel, ContentMessage } from 'axway-react-ui';
import cn from 'classnames';
import NodeConfigField from './NodeConfigField';

const UNSUPPORTED_TYPES = [ 'null', 'boolean' ];

const compareParameters = (a, b) => {
	if (a.required && !b.required) {
		return -1;
	}
	if (!a.required && b.required) {
		return 1;
	}
	return a.name.localeCompare(b.name);
};

/**
 * @param {object} [schemas={}] - A set of JSON schemas keyed by $ref ID used
 * to dereference schema.
 */
function expandRef(schema, schemas) {
	if (schema && schema.$ref && schemas.hasOwnProperty(schema.$ref)) {
		schema = schemas[schema.$ref];
	} else if (schema && schema.oneOf) {
		// RDPP-5432: We are adding support for oneOf.
		// If schema is of type oneOf search for reference within objects.
		const types = schema.oneOf;
		types.forEach((type, index) => {
			if (type.$ref && schemas.hasOwnProperty(type.$ref)) {
				types[index] = schemas[type.$ref];
			}
		});
	}
	return schema;
}

/**
 * Builds an object of key/value built from an array of flow parameter inputs.
 *
 * Builds a map of parameters keyed from the name.
 * @param {object} flowParameters - A an array of parameters from a flow,
 * see flowschema-v4.json#/definition/parameters
 * @returns {object} key: Parameter name (id), value: The value of the
 * parameter.
 */
function getMapOfParametersByID(flowParameters) {
	const paramsByID = {};
	for (const param of flowParameters) {
		paramsByID[param.name] = param;
	}
	return paramsByID;
}

/**
 * Checks if the specific field has an invalidation.
 *
 * @param {string} type - One of: outputsInvalid, paramsInvalid,
 * or authorizationInvalid
 * @param {string} name - Name of the field to check if invalid.
 * @param {object} [errors] - Validation errors.
 * @return {object} Return object if invalid.
 */
function getFieldValidation({ type, name, errors }) {
	return errors && errors[type] && errors[type][name];
}

/**
 * Gets a set of known parameters.
 * @param {object} inputParameters - The flow-node method input parameter,
 * see flownodeschema-v2.json#/definitions/inputParameters
 * @param {object} flowParameters - A an array of parameters from a flow,
 * see flowschema-v4.json#/definition/parameters
 * @param {object} options - Additional options.
 * @param {object} options.id - The graph node's unique identifier.
 * @param {object} [options.errors] - A set of validation errors.
 * @param {object} [options.schemas] - A set of JSON schemas keyed by
 * $ref ID used to dereference schema.
 * @param {object[]} An array of known parameters.
 */
function getKnownParams(inputParameters, flowParameters, options) {
	const paramsByID = getMapOfParametersByID(flowParameters);

	const { errors, schemas } = options;
	const knownParams = [];
	for (const name in inputParameters.properties) {
		const paramSpec = inputParameters.properties[name];
		const schema = expandRef(paramSpec, schemas);
		const invalid = getFieldValidation({
			type: 'paramsInvalid',
			name,
			errors
		});

		const {
			type,
			value
		} = paramsByID && { type: 'jsonpath', ...paramsByID[name] };
		const enabled = !!(paramsByID && paramsByID.hasOwnProperty(name));

		const required = inputParameters.required
			&& inputParameters.required.includes(name);

		// If we have no value then we should pick the type to show
		// If the spec defines an initialType we will use that if we can
		const initialType = schema.options && schema.options.initialType;

		let valueType = (initialType && value === undefined)
			? initialType : type;
		// This code checks `valueType` for an edge case where we cannot
		// render an enum with a missing value.  E.g. if we had a string
		// enum of "red", "green", "blue", and the value is undefined and
		// the initialType is string, then ideally, we'd render the
		// drop-down.  However, value is undefined, so there is no actual
		// enum value we could pick in order to render the drop-down.
		// Best we can do is switch the type (e.g. jsonpath).  The same
		// initial value issue exists for null and boolean.
		if (required && initialType) {
			// Do not allow certain types since we're not able to correctly
			// display a "missing value" for their type of input.  We only
			// care about enums types that we currently support rendering
			// as a drop-down.
			if ((schema.hasOwnProperty('enum')
				&& NodeConfigField.supportedEnumTypes.includes(valueType))
				|| UNSUPPORTED_TYPES.includes(valueType)) {
				// Revert back to using the type from the flow if the
				// `initialType` is not allowed
				valueType = type;
			}
		}

		knownParams.push({
			id: name,
			key: name,
			name: paramSpec.title || name,
			required,
			schema,
			description: paramSpec.description,
			group: paramSpec.group,
			enabled,
			value: value,
			valueType,
			// may be used for a special field or editor i.e. javascript
			valueFormat: paramSpec.format,
			invalid
		});
	}
	return knownParams;
}

/**
 * Builds a list of all the unknown parameters that can be resolved in the UI.
 * Unknown parameters are displayed in the same way as the rest of the method
 * parameters, with the following restrictions:
 *  - Not editable
 *  - Red background to draw attention to the problem
 *  - Can be deleted
 * An example of parameter that is considered invalid is one that no longer
 * exists - either due to renaming or deletion. The user is required to
 * manually delete it from the UI to "fix" the problem and is presented with
 * the ability to copy it's old value.
 *
 * @param {object} flowParameters - A an array of parameters from a flow,
 * see flowschema-v4.json#/definition/parameters
 * @param {object} options - Additional options.
 * @param {object} [options.errors] - A set of validation errors.
 * @returns {object[]} A list of all parameters that are in the flow but are
 * not in the flow-node definition.
 */
function getUnknownParams(flowParameters, options) {
	const { errors } = options;
	// All the unknown parameters in the current node have
	// NOT_IN_SPEC errors.
	const paramsInvalid = errors && errors.paramsInvalid;
	if (!paramsInvalid) {
		// There aren't any invalid parameters
		return [];
	}

	const paramsByID = getMapOfParametersByID(flowParameters);
	const unknownParams = [];
	for (const paramID in paramsInvalid) {
		if (paramsInvalid[paramID] !== 'NOT_IN_SPEC') {
			continue;
		}

		// Check if the parameter
		const parameter = paramsByID[paramID];
		if (!parameter) {
			continue;
		}

		const {
			value,
			type,
			metaName,
			metaDescription
		} = parameter;

		unknownParams.push({
			id: paramID,
			key: paramID,
			name: metaName || paramID,
			required: false,
			schema: {},
			enabled: true,
			value,
			valueType: type || 'jsonpath',
			description: metaDescription,
			// NOTE: this is specific to triggers, but this isn't what we
			// want here.  something to consider for a new component or
			// refactoring later.
			invalid: 'TRIGGER_PARAM_NOT_IN_SPEC',
			// Makes the field readOnly
			readOnly: true,
			// Applies the corresponding styles for unknown parameters.
			deletable: true
		});
	}
	return unknownParams;
}

/**
 * Generates properties suitable for passing to `NodeConfigField`.  Given
 * sets of parameters definitions (`inputParameters` and `authzParameters`),
 * it iterates over these, combining them with values provided by
 * `flowParameters` to produce `NodeConfigField` properties.  It also uses
 * the validation `errors` to identify those parameters that are invalid
 * and generates suitable properties for them as well.
 *
 * It organizes the parameters into a response containing three separate
 * groups: `{ auth, general, groups }`.
 *
 * @param {object} options - All options.
 * @param {object} options.id - The graph node's unique identifier.
 * @param {object} options.inputParameters - The flow-node method input
 * parameter, see flownodeschema-v2.json#/definitions/inputParameters
 * @param {object} options.authzParameters - The flow-node method auth
 * parameter, see flownodeschema-v2.json#/definitions/inputParameters
 * @param {object} options.flowParameters - A an array of parameters
 * from a flow, see flowschema-v4.json#/definition/parameters
 * @param {object} options.schemas - A set of JSON schemas keyed by
 * $ref ID used to dereference schema.
 * @param {object} options.errors - A set of validation errors.
 * @return {object} An object of `{ auth, general, groups }` each having
 * an array of `NodeConfigField` properties.
 */
function getParams(options) {
	const {
		inputParameters,
		flowParameters,
		authzParameters,
		schemas,
		errors
	} = options;

	const knownParams = getKnownParams(inputParameters, flowParameters, {
		schemas,
		errors
	});
	const authzParams = getKnownParams(authzParameters, flowParameters, {
		schemas,
		errors
	});
	const unknownParams = getUnknownParams(flowParameters, {
		schemas,
		errors
	});

	const params = {
		auth: authzParams,
		general: unknownParams,
		groups: {}
	};

	return knownParams.reduce((acc, cur) => {
		// split the known parameters into ungrouped "general" or those
		// grouped by name.
		if (cur.group) {
			if (!acc[cur.group]) {
				acc[cur.group] = [];
			}
			if (!acc.groups[cur.group]) {
				acc.groups[cur.group] = [];
			}
			acc.groups[cur.group].push(cur);
			acc.groups[cur.group].sort(compareParameters);
		} else {
			acc.general.push(cur);
			acc.general.sort(compareParameters);
		}
		return acc;
	}, params);
}

function renderNodeConfigFields(params, options) {
	const {
		selectors,
		onEnable,
		onChange,
		onDelete
	} = options;

	return params.map((param) => {
		if (param.deletable) {
			param.onDelete = onDelete;
		}
		if (!onChange && !onEnable) {
			// if there are no callbacks, make it read-only
			param.readOnly = true;
			param.enabled = undefined; // disables toggle (don't ask)
		}
		if (!param.readOnly) {
			param.onChange = onChange;
		}
		if (!param.required && !param.readOnly) {
			param.onEnable = onEnable;
		}
		param.selectors = selectors;
		return <NodeConfigField {...param} />;
	});
}

/**
 * Renders "Parameters" tab.  Given two sets of parameters definitions,
 * i.e. `inputParameters` and `authzParameters`, it iterates over these,
 * combining them with values provided by `flowParameters` to produce
 * `NodeConfigField` properties.  It also uses the validation `errors` to
 * identify those parameters that are invalid and generates suitable
 * properties for them as well.
 *
 * NOTE: Unfortunately, the `Tab` cannot be wrapped in another class,
 * otherwise it would have (maybe there's a way to do it).
 *
 * @param {string} id - The graph node's unique identifier.
 * @param {string} title - The title of the parameters tab, e.g. "Parameters".
 * @param {object} inputParameters - The flow-node method input parameter (see
 * flownodeschema-v2.json#/definitions/inputParameters)
 * @param {string} options.emptyMessage - The message to render when there are
 * no parameters.
 * @param {object} [flowParameters=[]] - A an array of parameters from a flow,
 * see flowschema-v4.json#/definition/parameters
 * @param {object} [authzParameters={}] - The flow-node method auth parameter (see
 * flownodeschema-v2.json#/definitions/inputParameters)
 * @param {object} [schemas={}] - A set of JSON schemas keyed by $ref ID used
 * to dereference schema.
 * @param {object} [options.errors={}] - A set of validation errors.
 * @return {Tab} The parameters tab.
 */
function renderParametersTab(options) {
	let { title } = options;
	const {
		inputParameters,
		flowParameters = [],
		authzParameters = {},
		schemas = {},
		errors = {},
		emptyMessage
	} = options;

	const params = getParams({
		inputParameters,
		flowParameters,
		authzParameters,
		schemas,
		errors
	});

	const generalParams = renderNodeConfigFields(params.general, options);
	const groups = Object.keys(params.groups).sort();

	const invalidParams = errors && !!errors.paramsInvalid;

	if (invalidParams) {
		title = (
			<div>
				{title}{<span className="node-status-icon gg icon-GG-warning" />}
			</div>
		);
	}

	if (!generalParams.length && !groups.length) {
		return (
			<Tab eventKey="parameters" title={title}>
				<ContentMessage
					detail={emptyMessage}
					imgClass="gg icon-GG-empty-list"
					className="sm empty"
					inlineContent
				/>
			</Tab>
		);
	}

	if (!groups.length) {
		return (
			<Tab eventKey="parameters" title={title}>
				{generalParams}
			</Tab>
		);
	}

	// There are grouped parameters
	const panels = generalParams;
	for (const name of groups) {
		// expand the group if any parameter is enabled, has a value, or is
		// invalid
		const expand = !!params.groups[name]
			.find(a => a.enabled || a.required || a.value || a.invalid);
		const error = !!params.groups[name].find(a => a.invalid);
		const header = (
			<span key={name} className={cn('group-header', { error })}>
				{name}
				{ error && <a className="gg icon-GG-warning" /> }
			</span>
		);
		panels.push(
			<Accordion id={name} key={name}>
				<Panel
					independent
					hasIcon
					defaultExpanded={expand}
					header={header}
					bsStyle="default"
					animate
					borders={false}
				>
					{renderNodeConfigFields(params.groups[name], options)}
				</Panel>
			</Accordion>
		);
	}
	return (
		<Tab eventKey="parameters" title={title}>
			{panels}
		</Tab>
	);
}

export default renderParametersTab;
